import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { getProductScope } from '@discover-frontend/utils';

import {
  useStanleyQueryCredits,
  useStanleyQueryData,
  useStanleyQueryHistoryItem,
} from '../../../api/hooks';
import { STANLEY_QUERY_KEYS } from '../../../api/constants';
import { isStanleyQueryCreditsExpiredError } from '../../../api/errors';
import {
  StanleyQueryRequestPayload,
  StanleyQueryResponse,
} from '../../../api/types';

export const PANEL_VIEWS = {
  FORM: 'form',
  RESULTS: 'results',
  HISTORY: 'history',
} as const;

export type StanleyPanelView = (typeof PANEL_VIEWS)[keyof typeof PANEL_VIEWS];

const HISTORY_ITEM_ACTIONS = {
  VIEW: 'view',
  EDIT: 'edit',
} as const;

export type StanleyHistoryItemAction =
  (typeof HISTORY_ITEM_ACTIONS)[keyof typeof HISTORY_ITEM_ACTIONS];

export interface StanleyQueryPanelContextValue {
  view: StanleyPanelView;
  setView: (next: StanleyPanelView) => void;
  selectedHistoryRunId: string | null;
  setSelectedHistoryRunId: (runId: string | null) => void;
  handleSelectHistoryItem: (runId: string) => void;
  handleEditHistoryItem: (runId: string) => void;
  formResetKey: number;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  lastSubmittedPrompt: string;
  stanleyQueryData: StanleyQueryResponse | null;
  resetStanleyQuery: () => void;
  handleNewQuery: () => void;
  handleEditPrompt: () => void;
  handleTryAgain: () => void;
  handlePostStanleyQuery: (payload: StanleyQueryRequestPayload) => void;
  isFetchingHistoryItem: boolean;
  creditsRemaining: number | null;
  isCreditsFree: boolean;
  isCreditsLoading: boolean;
  isCreditsError: boolean;
  isCreditsExpired: boolean;
}

const StanleyQueryPanelContext =
  createContext<StanleyQueryPanelContextValue | null>(null);

export interface StanleyQueryPanelProviderProps {
  children: React.ReactNode;
  initialView?: StanleyPanelView;
  openHistoryRequestId?: number;
  onViewChange?: (view: StanleyPanelView) => void;
}

export const StanleyQueryPanelProvider: FC<StanleyQueryPanelProviderProps> = ({
  children,
  initialView = PANEL_VIEWS.FORM,
  openHistoryRequestId = 0,
  onViewChange,
}) => {
  const queryClient = useQueryClient();
  const scope = getProductScope();
  const creditsKey = [STANLEY_QUERY_KEYS.GET_CREDITS, scope];

  const {
    mutate: postStanleyQueryMutate,
    loadFromHistory,
    isLoading,
    isError,
    error,
    data: stanleyQueryData,
    reset: resetStanleyQuery,
  } = useStanleyQueryData();

  const [view, setView] = useState<StanleyPanelView>(initialView);
  const [selectedHistoryRunId, setSelectedHistoryRunId] = useState<
    string | null
  >(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [lastSubmittedPrompt, setLastSubmittedPrompt] = useState('');
  const [historyItemAction, setHistoryItemAction] =
    useState<StanleyHistoryItemAction | null>(null);
  const prevOpenHistoryRequestIdRef = useRef(openHistoryRequestId);

  useEffect(() => {
    onViewChange?.(view);
  }, [view, onViewChange]);

  useEffect(() => {
    if (openHistoryRequestId === prevOpenHistoryRequestIdRef.current) {
      return;
    }

    prevOpenHistoryRequestIdRef.current = openHistoryRequestId;
    setSelectedHistoryRunId(null);
    setView(PANEL_VIEWS.HISTORY);
  }, [openHistoryRequestId]);

  const { data: historyItem, isFetching: isFetchingHistoryItem } =
    useStanleyQueryHistoryItem(selectedHistoryRunId, {
      enabled: Boolean(selectedHistoryRunId),
    });

  const {
    data: creditsData,
    isLoading: isCreditsLoading,
    isError: isCreditsError,
  } = useStanleyQueryCredits();

  const creditsRemaining = creditsData?.remaining ?? null;
  const isCreditsFree = creditsData?.isFree ?? false;

  const isCreditsExpired =
    !isCreditsFree &&
    !isCreditsLoading &&
    !isCreditsError &&
    creditsRemaining === 0;

  useEffect(() => {
    if (
      !isError ||
      !isStanleyQueryCreditsExpiredError(error) ||
      isCreditsFree
    ) {
      return;
    }

    queryClient.setQueryData(creditsKey, { remaining: 0, isFree: false });
    resetStanleyQuery();
  }, [
    creditsKey,
    error,
    isCreditsFree,
    isError,
    queryClient,
    resetStanleyQuery,
  ]);

  useEffect(() => {
    if (stanleyQueryData && !isLoading) {
      setView(PANEL_VIEWS.RESULTS);
    }
  }, [stanleyQueryData, isLoading]);

  const handleSelectHistoryItem = useCallback((runId: string) => {
    setHistoryItemAction(HISTORY_ITEM_ACTIONS.VIEW);
    setSelectedHistoryRunId(runId);
  }, []);

  const handleEditHistoryItem = useCallback((runId: string) => {
    setHistoryItemAction(HISTORY_ITEM_ACTIONS.EDIT);
    setSelectedHistoryRunId(runId);
  }, []);

  const handleNewQuery = useCallback(() => {
    resetStanleyQuery();
    setLastSubmittedPrompt('');
    setFormResetKey((k) => k + 1);
    setSelectedHistoryRunId(null);
    setHistoryItemAction(null);
    setView(PANEL_VIEWS.FORM);
  }, [resetStanleyQuery]);

  const handleEditPrompt = useCallback(() => {
    resetStanleyQuery();
    setFormResetKey((k) => k + 1);
    setSelectedHistoryRunId(null);
    setHistoryItemAction(null);
    setView(PANEL_VIEWS.FORM);
  }, [resetStanleyQuery]);

  const handleTryAgain = useCallback(() => {
    resetStanleyQuery();
    setView(PANEL_VIEWS.FORM);
  }, [resetStanleyQuery]);

  const handlePostStanleyQuery = useCallback(
    (payload: StanleyQueryRequestPayload) => {
      if (isCreditsExpired) {
        return;
      }

      setLastSubmittedPrompt(payload.user_prompt);
      postStanleyQueryMutate(payload);
    },
    [isCreditsExpired, postStanleyQueryMutate]
  );

  useEffect(() => {
    if (!selectedHistoryRunId || !historyItem || !historyItemAction) return;

    const prompt = historyItem.summary ?? '';

    if (historyItemAction === HISTORY_ITEM_ACTIONS.EDIT) {
      resetStanleyQuery();
      setLastSubmittedPrompt(prompt);
      setFormResetKey((k) => k + 1);
      setView(PANEL_VIEWS.FORM);
    } else {
      setLastSubmittedPrompt(prompt);
      loadFromHistory(historyItem);
      setView(PANEL_VIEWS.RESULTS);
    }

    setSelectedHistoryRunId(null);
    setHistoryItemAction(null);
  }, [
    historyItem,
    historyItemAction,
    loadFromHistory,
    resetStanleyQuery,
    selectedHistoryRunId,
  ]);

  const value = useMemo<StanleyQueryPanelContextValue>(
    () => ({
      view,
      setView,
      selectedHistoryRunId,
      setSelectedHistoryRunId,
      handleSelectHistoryItem,
      handleEditHistoryItem,
      formResetKey,
      isLoading,
      isError,
      error,
      lastSubmittedPrompt,
      stanleyQueryData,
      resetStanleyQuery,
      handleNewQuery,
      handleEditPrompt,
      handleTryAgain,
      handlePostStanleyQuery,
      isFetchingHistoryItem,
      creditsRemaining,
      isCreditsFree,
      isCreditsLoading,
      isCreditsError,
      isCreditsExpired,
    }),
    [
      view,
      selectedHistoryRunId,
      handleSelectHistoryItem,
      handleEditHistoryItem,
      formResetKey,
      isLoading,
      isError,
      error,
      lastSubmittedPrompt,
      stanleyQueryData,
      resetStanleyQuery,
      handleNewQuery,
      handleEditPrompt,
      handleTryAgain,
      handlePostStanleyQuery,
      isFetchingHistoryItem,
      creditsRemaining,
      isCreditsFree,
      isCreditsLoading,
      isCreditsError,
      isCreditsExpired,
    ]
  );

  return (
    <StanleyQueryPanelContext.Provider value={value}>
      {children}
    </StanleyQueryPanelContext.Provider>
  );
};

export const useStanleyQueryPanel = (): StanleyQueryPanelContextValue => {
  const ctx = useContext(StanleyQueryPanelContext);
  if (!ctx) {
    throw new Error(
      'useStanleyQueryPanel must be used within StanleyQueryPanelProvider'
    );
  }
  return ctx;
};
